import { BookingEvent } from "../../hooks/useBookingEvents";
import { formatDateToTime12h } from "../../utils/date";

export interface KanbanBlock {
  /** Time range heading shown above the block. */
  label: string;
  /** Inclusive start hour of the bucket. */
  startHour: number;
  /** Exclusive end hour of the bucket. */
  endHour: number;
}

const blockLabel = (
  startHour: number,
  startMinute: number,
  endHour: number,
  endMinute: number,
): string => {
  const start = new Date(2000, 0, 1, startHour, startMinute, 0);
  const end = new Date(2000, 0, 1, endHour, endMinute, 0);
  return `${formatDateToTime12h(start)} - ${formatDateToTime12h(end)}`;
};

/**
 * Buckets are contiguous (00:00-08:59, 09:00-16:59, 17:00-23:59) so no booking can
 * fall between blocks; the labels are the ranges the business quotes.
 */
export const KANBAN_BLOCKS: KanbanBlock[] = [
  { label: blockLabel(0, 0, 8, 59), startHour: 0, endHour: 9 },
  { label: blockLabel(9, 0, 16, 59), startHour: 9, endHour: 17 },
  { label: blockLabel(17, 0, 23, 59), startHour: 17, endHour: 24 },
];

const byStartThenId = (a: BookingEvent, b: BookingEvent) =>
  a.start.getTime() - b.start.getTime() ||
  Number(a.resource?.id ?? 0) - Number(b.resource?.id ?? 0);

/** Split events into one array per block, each sorted by start time then booking id. */
export const groupBookingsIntoBlocks = (
  events: BookingEvent[],
): BookingEvent[][] => {
  const buckets: BookingEvent[][] = KANBAN_BLOCKS.map(() => []);

  events.forEach((event) => {
    const hour = event.start.getHours();
    const index = KANBAN_BLOCKS.findIndex(
      (block) => hour >= block.startHour && hour < block.endHour,
    );
    if (index >= 0) {
      buckets[index].push(event);
    }
  });

  return buckets.map((bucket) => bucket.sort(byStartThenId));
};

/** Cards per block per row — must match the grid in styles.css. */
export const KANBAN_CARDS_PER_ROW = 3;

/** Full-page width on the timeline printout (three blocks × three cards). */
export const KANBAN_TIMELINE_CARDS_PER_ROW =
  KANBAN_CARDS_PER_ROW * KANBAN_BLOCKS.length;

/** Chronological order shared by in-block sorting and the timeline print layout. */
export const sortEventsByTime = (events: BookingEvent[]): BookingEvent[] =>
  [...events].sort(byStartThenId);

/**
 * Transpose the blocks into printable table rows. Row N holds one slice per
 * block, each as wide as the on-screen grid, so the three columns stay under
 * their repeated labels and break across pages together.
 */
export const buildKanbanPrintRows = (
  blocks: BookingEvent[][],
  cardsPerRow: number = KANBAN_CARDS_PER_ROW,
): BookingEvent[][][] => {
  const rowCount = Math.max(
    0,
    ...blocks.map((bucket) => Math.ceil(bucket.length / cardsPerRow)),
  );

  return Array.from({ length: rowCount }, (_, row) =>
    blocks.map((bucket) =>
      bucket.slice(row * cardsPerRow, (row + 1) * cardsPerRow),
    ),
  );
};

/** One row per time-ordered slice — no block columns, full page width. */
export const buildTimelinePrintRows = (
  events: BookingEvent[],
  cardsPerRow: number = KANBAN_TIMELINE_CARDS_PER_ROW,
): BookingEvent[][] => {
  const sorted = sortEventsByTime(events);
  if (sorted.length === 0) {
    return [];
  }

  return Array.from(
    { length: Math.ceil(sorted.length / cardsPerRow) },
    (_, row) => sorted.slice(row * cardsPerRow, (row + 1) * cardsPerRow),
  );
};
